import React, { useEffect, useState } from 'react';
import {
  Button,
  Tabs,
  Text,
} from 'components';
import {
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
} from 'antd';
import { formatReceived } from 'utils/formatters';
import { useSelector, useDispatch } from 'react-redux';
import { selectEntidad, selectUser } from 'store/auth';
import {
  InfoCircleOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  SaveOutlined,
  EditOutlined,
  MailOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import _logger from 'utils/logger';
import {
  getRegimenesFiscales,
  patchCiudadanoPublic,
  patchContribuyentePublic,
} from 'api/padrones';
import {
  AUTH_CONTRIBUYENTE,
  REPUVE,
} from 'utils/env';
import RegisterPadron from 'views/Payments/components/RegisterPadron';
import Direccion from 'components/Direccion/index';

const { TabPane } = Tabs;

const MisDatos = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const entidad = useSelector(selectEntidad);

  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [formContacto] = Form.useForm();
  const [formDireccion] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [isEditing, setIsEditing] = useState(false);
  const [regimenesFiscales, setRegimenesFiscales] = useState([]);
  const editingAddress = isEditing && currentTabKey === 'direccion';

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      const requests = AUTH_CONTRIBUYENTE && user?.regimen_fiscal
        ? [getRegimenesFiscales({ id: user?.regimen_fiscal })] : [];

      const [_regimenes = []] = await Promise.all(requests);
      if (mounted) {
        setRegimenesFiscales(_regimenes);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const setValues = () => {
    const values = formatReceived(user);
    form.setFieldsValue(values);
    formInfo.setFieldsValue(values);
    formContacto.setFieldsValue(values);
  };

  useEffect(() => {
    setValues();
  }, [user]);

  const onFinish = async () => {
    try {
      setLoading(true);
      if (editingAddress) {
        await formDireccion.validateFields();
      } else {
        await form.validateFields();
        await formInfo.validateFields();
        await formContacto.validateFields();
      }
      const values = editingAddress ? {
        direcciones: formDireccion.getFieldsValue(),
      } : {
        ...form.getFieldsValue(),
        ...formInfo.getFieldsValue(),
        ...formContacto.getFieldsValue(),
      };
      delete values.regimen_fiscal;
      if (AUTH_CONTRIBUYENTE) {
        await patchContribuyentePublic(user.id, values, dispatch);
      } else {
        await patchCiudadanoPublic(user.id, values, dispatch);
      }
      setIsEditing(false);
      setLoading(false);
    } catch (error) {
      _logger(error);
      setLoading(false);
      if (error.errorFields) {
        const forms = [form, formInfo, formContacto, formDireccion].map((f) => ({
          tabKey: f?.__INTERNAL__?.name,
          keys: f?.getFieldsError()?.map((e) => e.name[0]),
        }));
        const errorKeys = error.errorFields.map((e) => e.name[0]);
        const formWithErrors = forms.find((f) => f.keys.some((k) => errorKeys.includes(k)));
        if (formWithErrors) setCurrentTabKey(formWithErrors.tabKey);
      }
    }
  };

  const onCancel = () => {
    setIsEditing(false);
    setValues();
  };

  return (
    <Spin spinning={loading} tip="Cargando...">
      <Row>
        <Col span={24}>
          <Tabs activeKey={currentTabKey} onChange={setCurrentTabKey} type="card">
            <TabPane
              tab={(
                <span>
                  <InfoCircleOutlined />
                  Información General
                </span>
              )}
              key="general"
              disabled={editingAddress}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={() => onFinish()}
                name="general"
                onValuesChange={(changedValues) => {
                  if ('tipo_de_persona' in changedValues) {
                    formInfo.setFieldValue('tipo_de_persona', changedValues.tipo_de_persona);
                  }
                }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name={AUTH_CONTRIBUYENTE ? 'clave' : 'clave_ciudadana'}
                      label={AUTH_CONTRIBUYENTE ? 'Clave' : 'Clave ciudadana'}
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <RegisterPadron
                    tipoDePadron={AUTH_CONTRIBUYENTE ? 15 : 1}
                    callback={() => null}
                    onCancel={() => null}
                    onlyItems
                    selfId={user.id}
                    loading={loading}
                    disabled={loading || !isEditing}
                    general
                    padron={user}
                  />
                  {AUTH_CONTRIBUYENTE && !!user.regimen_fiscal && !REPUVE && (
                    <Col xs={24} sm={24} md={8} order={10}>
                      <Form.Item
                        name="regimen_fiscal"
                        label="Régimen fiscal (SAT)"
                      >
                        <Select options={regimenesFiscales} disabled />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </Form>
            </TabPane>
            <TabPane
              tab={(
                <span>
                  <IdcardOutlined />
                  Información de identidad
                </span>
              )}
              key="identidad"
              disabled={editingAddress}
            >
              <Form
                form={formInfo}
                layout="vertical"
                onFinish={() => onFinish()}
                name="identidad"
                onValuesChange={(changedValues) => {
                  if ('tipo_de_persona' in changedValues) {
                    form.setFieldValue('tipo_de_persona', changedValues.tipo_de_persona);
                  }
                }}
              >
                <Row gutter={10}>
                  <RegisterPadron
                    tipoDePadron={AUTH_CONTRIBUYENTE ? 15 : 1}
                    callback={() => null}
                    onCancel={() => null}
                    onlyItems
                    selfId={user.id}
                    loading={loading}
                    disabled={loading || !isEditing}
                    identidad
                    padron={user}
                  />
                </Row>
              </Form>
            </TabPane>
            <TabPane
              tab={(
                <span>
                  <MailOutlined />
                  Información de contacto
                </span>
              )}
              key="contacto"
              disabled={editingAddress}
            >
              <Form
                form={formContacto}
                layout="vertical"
                onFinish={() => onFinish()}
                name="contacto"
              >
                <Row gutter={10}>
                  <RegisterPadron
                    tipoDePadron={AUTH_CONTRIBUYENTE ? 15 : 1}
                    callback={() => null}
                    onCancel={() => null}
                    onlyItems
                    selfId={user.id}
                    loading={loading}
                    disabled={loading || !isEditing}
                    contacto
                    padron={user}
                  />
                </Row>
              </Form>
            </TabPane>
            {AUTH_CONTRIBUYENTE && (
              <TabPane
                tab={(
                  <span>
                    <EnvironmentOutlined />
                    Dirección Fiscal
                  </span>
                )}
                key="direccion"
                disabled={isEditing && !editingAddress}
              >
                <Form
                  form={formDireccion}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  name="direccion"
                  initialValues={{
                    pais: 'México',
                    estado: entidad.direccion?.codigo_postal.municipio.estado.nombre_de_AGEE,
                  }}
                  key={isEditing}
                >
                  <Direccion
                    isEditing={isEditing}
                    propName="direcciones"
                    setLoading={setLoading}
                    fatherObject={user}
                  />
                </Form>
              </TabPane>
            )}
          </Tabs>
        </Col>
      </Row>
      <br />
      <Row justify="end">
        {isEditing && (
          <Button
            onClick={onCancel}
            loading={loading}
            style={{ marginRight: 10 }}
          >
            <Text>Cancelar</Text>
            <UndoOutlined />
          </Button>
        )}
        <Button
          $accent
          onClick={() => (isEditing ? onFinish() : setIsEditing(true))}
          loading={loading}
        >
          <Text>{isEditing ? 'Guardar' : 'Editar'}</Text>
          {isEditing ? <SaveOutlined /> : <EditOutlined />}
        </Button>
      </Row>
    </Spin>
  );
};

export default MisDatos;
